import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Paper,
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  btnLeft: {
    display: "flex",
    marginRight: "auto",
    marginLeft: 12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  // ✅ CORREÇÃO: Estilos do modal seguindo padrão do NewTicketModal
  dialogTitle: {
    padding: theme.spacing(2, 3),
    flexShrink: 0,
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem'
    }
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    maxHeight: 'calc(100vh - 250px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  buttonPrimary: {
    background: theme.palette.type === 'dark' 
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.4)',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
        : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: theme.palette.type === 'dark' ? '#444' : '#ccc',
      color: theme.palette.type === 'dark' ? '#888' : '#666',
      boxShadow: 'none',
    },
    transition: 'all 0.3s ease',
  },
  buttonSecondary: {
    color: theme.palette.type === 'dark' ? '#fff' : theme.palette.primary.main,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.1)',
    },
  },
}));

const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  // projectName: Yup.string()
  //   .min(3, "Too Short!")
  //   .max(100, "Too Long!")
  //   .required(),
  // jsonContent: Yup.string().min(3, "Too Short!").required(),
  // language: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required(),
});

const QueueIntegration = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorModeContext = useContext(ColorModeContext);
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",
  };

  const [integration, setIntegration] = useState(initialState);

  useEffect(() => {
    (async () => {
      if (!integrationId) return;
      try {
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        setIntegration((prevState) => {
          return { ...prevState, ...data };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setIntegration({
        type: "dialogflow",
        name: "",
        projectName: "",
        jsonContent: "",
        language: "",
        urlN8N: "",
        typebotDelayMessage: 1000,
      });
    };
  }, [integrationId, open]);

  const handleClose = () => {
    onClose();
    setIntegration(initialState);
  };

  const handleTestSession = async (event, values) => {
    try {
      const { projectName, jsonContent, language } = values;

      await api.post(`/queueIntegration/testSession`, {
        projectName,
        jsonContent,
        language,
      });

      toast.success(i18n.t("queueIntegrationModal.messages.testSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDialogflow = async (values) => {
    try {
      if (
        values.type === "n8n" ||
        values.type === "webhook" ||
        values.type === "typebot" ||
        values.type === "flowbuilder"
      )
        values.projectName = values.name;
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, values);
        toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
      } else {
        await api.post("/queueIntegration", values);
        toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="body"
        PaperProps={{
          style: {
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            margin: '32px auto',
            maxHeight: 'calc(100vh - 64px)',
          },
        }}
      >
        <DialogTitle 
          className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
        >
          {integrationId
            ? `${i18n.t("queueIntegrationModal.title.edit")}`
            : `${i18n.t("queueIntegrationModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={DialogflowSchema}
          onSubmit={(values, actions, event) => {
            setTimeout(() => {
              handleSaveDialogflow(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <Paper square className={classes.mainPaper} elevation={1}>
                <DialogContent dividers className={classes.dialogContent}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="type-selection-input-label">
                          {i18n.t("queueIntegrationModal.form.type")}
                        </InputLabel>

                        <Field
                          as={Select}
                          label={i18n.t("queueIntegrationModal.form.type")}
                          name="type"
                          labelId="profile-selection-label"
                          error={touched.type && Boolean(errors.type)}
                          helpertext={touched.type && errors.type}
                          id="type"
                          required
                        >
                          <MenuItem value="dialogflow">DialogFlow</MenuItem>
                          <MenuItem value="n8n">N8N</MenuItem>
                          <MenuItem value="webhook">WebHooks</MenuItem>
                          <MenuItem value="typebot">Typebot</MenuItem>
                          <MenuItem value="flowbuilder">Flowbuilder</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    {values.type === "dialogflow" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel id="language-selection-input-label">
                              {i18n.t("queueIntegrationModal.form.language")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t(
                                "queueIntegrationModal.form.language"
                              )}
                              name="language"
                              labelId="profile-selection-label"
                              fullWidth
                              error={
                                touched.language && Boolean(errors.language)
                              }
                              helpertext={touched.language && errors.language}
                              id="language-selection"
                              required
                            >
                              <MenuItem value="pt-BR">Portugues</MenuItem>
                              <MenuItem value="en">Inglês</MenuItem>
                              <MenuItem value="es">Español</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.projectName"
                            )}
                            name="projectName"
                            error={
                              touched.projectName && Boolean(errors.projectName)
                            }
                            helpertext={
                              touched.projectName && errors.projectName
                            }
                            fullWidth
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.jsonContent"
                            )}
                            type="jsonContent"
                            multiline
                            //inputRef={greetingRef}
                            maxRows={5}
                            minRows={5}
                            fullWidth
                            name="jsonContent"
                            error={
                              touched.jsonContent && Boolean(errors.jsonContent)
                            }
                            helpertext={
                              touched.jsonContent && errors.jsonContent
                            }
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                      </>
                    )}

                    {(values.type === "n8n" || values.type === "webhook") && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            required
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                      </>
                    )}

                    {values.type === "flowbuilder" && (
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.name")}
                          autoFocus
                          name="name"
                          fullWidth
                          error={touched.name && Boolean(errors.name)}
                          helpertext={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                        />
                      </Grid>
                    )}

                    {values.type === "typebot" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotSlug"
                            )}
                            name="typebotSlug"
                            error={
                              touched.typebotSlug && Boolean(errors.typebotSlug)
                            }
                            helpertext={
                              touched.typebotSlug && errors.typebotSlug
                            }
                            required
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotExpires"
                            )}
                            name="typebotExpires"
                            error={
                              touched.typebotExpires &&
                              Boolean(errors.typebotExpires)
                            }
                            helpertext={
                              touched.typebotExpires && errors.typebotExpires
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotDelayMessage"
                            )}
                            name="typebotDelayMessage"
                            error={
                              touched.typebotDelayMessage &&
                              Boolean(errors.typebotDelayMessage)
                            }
                            helpertext={
                              touched.typebotDelayMessage &&
                              errors.typebotDelayMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordFinish"
                            )}
                            name="typebotKeywordFinish"
                            error={
                              touched.typebotKeywordFinish &&
                              Boolean(errors.typebotKeywordFinish)
                            }
                            helpertext={
                              touched.typebotKeywordFinish &&
                              errors.typebotKeywordFinish
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordRestart"
                            )}
                            name="typebotKeywordRestart"
                            error={
                              touched.typebotKeywordRestart &&
                              Boolean(errors.typebotKeywordRestart)
                            }
                            helpertext={
                              touched.typebotKeywordRestart &&
                              errors.typebotKeywordRestart
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotUnknownMessage"
                            )}
                            name="typebotUnknownMessage"
                            error={
                              touched.typebotUnknownMessage &&
                              Boolean(errors.typebotUnknownMessage)
                            }
                            helpertext={
                              touched.typebotUnknownMessage &&
                              errors.typebotUnknownMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotRestartMessage"
                            )}
                            name="typebotRestartMessage"
                            error={
                              touched.typebotRestartMessage &&
                              Boolean(errors.typebotRestartMessage)
                            }
                            helpertext={
                              touched.typebotRestartMessage &&
                              errors.typebotRestartMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </DialogContent>
              </Paper>

              <DialogActions className={classes.dialogActions}>
                {values.type === "dialogflow" && (
                  <Button
                    //type="submit"
                    onClick={(e) => handleTestSession(e, values)}
                    color="inherit"
                    disabled={isSubmitting}
                    name="testSession"
                    variant="outlined"
                    className={classes.btnLeft}
                  >
                    {i18n.t("queueIntegrationModal.buttons.test")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  className={classes.buttonSecondary}
                  disabled={isSubmitting}
                  variant="text"
                >
                  {i18n.t("queueIntegrationModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  className={`${classes.buttonPrimary} ${classes.btnWrapper}`}
                  disabled={isSubmitting}
                  variant="contained"
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={16} style={{ marginRight: 8, color: '#fff' }} />
                      {integrationId
                        ? `${i18n.t("queueIntegrationModal.buttons.okEdit")}`
                        : `${i18n.t("queueIntegrationModal.buttons.okAdd")}`}
                    </>
                  ) : (
                    integrationId
                      ? `${i18n.t("queueIntegrationModal.buttons.okEdit")}`
                      : `${i18n.t("queueIntegrationModal.buttons.okAdd")}`
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QueueIntegration;
